import React from 'react';
import Label from '~/src/atoms/Label/';
import tw from 'twin.macro';
import PackCaret from '../../../public/bb2assets/images/svg/packCaret.svg';
import Button from '~/src/atoms/Button';
import Portal from '~/src/molecules/ClientPortal';
import useElementsPosition from '~/src/hooks/useElementsPosition';
import Link from '~/src/atoms/Link';
import CallToAction from '~/src/organisms/CallToAction';
import shouldShowMrp from '~/src/libs/shouldShowMrp';
import dynamic from 'next/dynamic';
import { DeckContext } from './DeckContext';
import { Listbox } from '@headlessui/react';
import { PDService } from '~/src/context/PD';
import { motion } from 'framer-motion';
import { variations } from '~/src/organisms/SKU/skuConfig';
import { triggerSnowplowEventProductInteractions } from '@bigbasket/snowplow';
import { CitrusAdsProductIntercations } from './SnowplowEvents';
import TickIcon from '../../../public/bb2assets/images/svg/TickIcon.svg';
import ClockFilledIcon from '~/public/bb2assets/svg/ClockFilledIcon.svg';
import ExpressIcon from '~/public/bb2assets/svg/ExpressIcon.svg';
import { calculateEtaForSku } from '~/src/utils/calculateEtaForSku';
import { useStore } from '~/src/hooks/useStore';
import { getItemFromSessionStorage } from '~/src/libs/sessionStorage';
import { mutate as swrMutate } from 'swr';

const PackSizeModal = dynamic(import('~/src/organisms/PackSizeModal'), { ssr: false });

const PackLabel = ({ packSizes = [], screenType, analyticsAttrs = {}, citrusAdId = {}, packSelected = {}, open = false, fromExtendedList }) => {
  const handleBlur = () => {
    if (open) {
      const eventData = {
        EventName: 'packsize_bottomsheet_closed',
        ScreenType: screenType,
        selectedSKU: packSelected,
        analyticsAttrs,
        citrusAdId,
        AdditionalInfo2: packSelected?.id && packSelected.id?.toString(),
        ...(fromExtendedList ? { InPageContext: 'extended_list' } : {}),
      };
      CitrusAdsProductIntercations(eventData);
    } else {
      const eventData = {
        EventName: 'packsize_bottomsheet_open',
        ScreenType: screenType,
        selectedSKU: packSelected,
        analyticsAttrs,
        citrusAdId,
        ...(fromExtendedList ? { InPageContext: 'extended_list', ScreenType: 'ps' } : {}),
      };
      CitrusAdsProductIntercations(eventData);
    }
  };

  return (
    <Button
      variant="silverSurfer-100-filled-large"
      onClick={handleBlur}
      css={[
        tw`w-full truncate pl-2 pr-0.5 py-0 h-7 xl:h-7.5 lg:text-sm xl:text-md leading-xs xl:leading-md flex justify-between items-center border`,
        open
          ? tw`text-white border-silverSurfer-600 bg-darkOnyx-600`
          : tw`bg-white text-darkOnyx-400 border-silverSurfer-300 xl:border-silverSurfer-400 hover:border-darkOnyx-800`,
      ]}>
      <Label css={tw`text-left truncate`}>{`${packSelected?.weight} ${packSelected?.packDesc ? `- ${packSelected?.packDesc}` : ''}`}</Label>
      {packSizes?.length > 1 && (
        <PackCaret className={open ? 'flex-shrink-0 transform rotate-180 fill-current fill-white' : 'flex-shrink-0 fill-current fill-darkOnyx-400'} />
      )}
    </Button>
  );
};

const ShowMoreCombo = ({ packSelected = {}, handleShowMore = () => {}, length = null }) => {
  return (
    <Listbox.Option value={null}>
      {packSelected?.combo?.destination?.display_name && (
        <Button
          onClick={() => handleShowMore(packSelected?.combo?.destination?.dest_ids)}
          variant="silverSurfer-100-filled-small"
          css={tw`p-2 mt-2.5 w-full text-md box-border border border-silverSurfer-200`}>
          {packSelected?.combo.destination.display_name}
        </Button>
      )}
      {length > 4 && (
        <Link href={packSelected?.absoluteUrl}>
          <Label css={tw`flex justify-center mt-2.5 font-semibold underline cursor-pointer text-rossoCorsa-500 text-md leading-xl`}>Show more</Label>
        </Link>
      )}
    </Listbox.Option>
  );
};

const Packs = ({
  open = false,
  packSizes = [],
  handleQuantityChange = () => {},
  handleShowMore = () => {},
  packSelected = {},
  portalsPosition,
  elementsPosition,
}) => {
  const packing = [...packSizes];
  packing.splice(packing[0].sort_index_pos + 1, 0, packing[0]);
  packing.splice(0, 1);
  const [hovering, setHovering] = React.useState({});
  const appHeader = useStore((state) => state.appHeader);

  return (
    open && (
      <Portal selector="listing-packsize-dropdown" styles={open && portalsPosition}>
        {
          <motion.div
            initial={{ opacity: 0, visibility: 'hidden' }}
            animate={{ opacity: 1, visibility: 'visible' }}
            className="absolute z-20"
            style={elementsPosition}>
            <Listbox.Options static className="p-2.5 mt-1 w-80 bg-white border border-solid border-silverSurfer-200 shadow-5 rounded-2xs focus:outline-none">
              {packing?.map((pack, i) => {
                const eta = calculateEtaForSku(pack?.visibility?.sa_id, appHeader?.sa_list);

                if (i < 4)
                  return (
                    <CallToAction key={pack?.id} payload={{ id: pack?.id, origin: '', invInfo:pack?.inv_info }} onChange={handleQuantityChange}>
                      {({ incQuantity, notifyMe }) => {

                        const handleBasketAdd = async () => {
                          await incQuantity();
                          const isIntegratedSA = JSON.parse(getItemFromSessionStorage('isIntegratedSA'));
                          swrMutate(`/order/v1/basket/detail/?is_split_order_supported=${isIntegratedSA}&offer_communication=true`);
                        };
                        
                        return (
                        <Listbox.Option value={pack} className="focus:outline-none">
                          <div
                            css={[tw`p-2.5 border rounded-3xs hover:shadow-2 cursor-pointer`, i && tw`mt-2.5`]}
                            className={`${packSelected?.id === pack?.id ? 'border-appleGreen-500' : 'border-silverSurfer-200'}`}
                            onMouseLeave={() => setHovering({ [pack?.id]: false })}
                            onMouseOver={() => setHovering({ [pack?.id]: true })}>
                            <div className="flex justify-between">
                              <div className="w-3/4 mb-1.5 truncate text-md leading-xss text-darkOnyx-800">
                                {`${pack.weight} ${pack.packDesc ? `- ${pack.packDesc}` : ''}`}
                              </div>
                              {packSelected?.id === pack.id && <TickIcon />}
                            </div>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center justify-center">
                                {pack.pricing.discount.d_text && (
                                  <Label
                                    css={tw`mr-2 flex items-center justify-center text-sm h-4.5 leading-xxs py-0.5 px-1.5 bg-appleGreen-50 text-appleGreen-700`}
                                    dangerouslySetInnerHTML={{ __html: pack.pricing.discount.d_text }}
                                  />
                                )}
                                {pack?.availability?.display_sp && (
                                  <Label
                                    css={tw`mr-1 font-semibold leading-sm text-darkOnyx-800 text-base`}>{`₹${pack.pricing.discount.prim_price?.sp}`}</Label>
                                )}
                                {pack?.availability?.display_mrp && shouldShowMrp(pack.pricing.discount.prim_price?.sp, pack.pricing.discount.mrp) && (
                                  <Label
                                    css={[
                                      tw`text-sm leading-none mr-1`,
                                      !pack.pricing.discount.d_avail == 'true'
                                        ? tw`font-semibold text-darkOnyx-800`
                                        : tw`font-light line-through text-darkOnyx-400`,
                                    ]}>
                                    {`₹${pack.pricing.discount.mrp}`}
                                  </Label>
                                )}

                                {eta && pack?.availability?.avail_status == '001' && (
                                  <div className="flex">
                                    {pack?.availability?.show_express ? (
                                      <div className="flex items-center bg-silverSurfer-100 rounded-s p-1 space-x-1">
                                        <ExpressIcon width="10px" height="10px" />
                                        <div className="text-slimyGreen-800 text-sm font-semibold leading-[13px] truncate uppercase">{eta}</div>
                                      </div>
                                    ) : (
                                      <div className="flex items-center bg-sunglow-100 rounded-s p-1 space-x-1">
                                        <ClockFilledIcon width="10px" height="10px" />
                                        <div className="text-sunglow-800 text-sm font-semibold leading-[13px] truncate uppercase">{eta}</div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>

                              {!pack?.availability?.not_for_sale && (
                                <div>
                                  {(pack?.availability?.avail_status === '010' || pack?.availability?.avail_status === '000') && (
                                    <Button
                                      variant="rossoCorsa-500-filled"
                                      css={[
                                        tw`h-5 px-1 text-sm text-white transform border-none tracking-xs rounded-3xs`,
                                        !hovering[pack?.id] && tw`bg-white text-darkOnyx-400 px-0`,
                                      ]}
                                      onClick={notifyMe}>
                                      {hovering[pack?.id] ? pack?.availability?.button : 'Out of stock'}
                                    </Button>
                                  )}
                                  {pack?.availability?.avail_status === '001' && (
                                    <Button
                                      className="add-button-list"
                                      variant="rossoCorsa-500-filled"
                                      css={[tw`w-10 h-5 text-sm duration-300 transform tracking-xs rounded-3xs`, !hovering[pack?.id] && tw`opacity-0`]}
                                      onClick={handleBasketAdd}>
                                      {pack?.availability?.button}
                                    </Button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </Listbox.Option>
                      )}
                    }
                    </CallToAction>
                  );
              })}
              <ShowMoreCombo packSelected={packSelected} handleShowMore={handleShowMore} length={packSizes?.length} />
            </Listbox.Options>
          </motion.div>
        }
      </Portal>
    )
  );
};

const PackChanger = ({ screenType, analyticsAttrs = {}, citrusAdId = {}, screenTypeId, ...rest }) => {
  const { packSizes = [], packSelected = {}, changePackSize, changeQuantity } = React.useContext(DeckContext);
  const [ref, portalsPosition, elementsPosition] = useElementsPosition(true, {
    topOffset: 30,
    leftOffset: 0,
    negativeLeftOffset: -95,
    elWidth: 300,
  });
  const [combo, setCombo] = React.useState({ open: false, ids: null });

  const handleQuantityChange = (e) => {
    if (e?.data?.status === 'OK') {
      changeQuantity(e?.data?.response?.sku?.tot_qty);
    }
  };

  const handleComboClose = () => {
    setCombo({ open: false, ids: null });
  };

  const handleShowMore = (ids) => {
    setCombo({ ...combo, ids, open: true });
  };

  const handlePackSizeChange = (value) => {
    changePackSize(value);
    triggerSnowplowEventProductInteractions({
      EventName: 'packsize_clicked',
      ScreenType: screenType,
      ScreenTypeID: Number(screenTypeId),
      SkuID: Number(packSelected?.id),
      AdditionalInfo2: `${value?.id}`,
    });
  };
  return (
    <div {...rest} ref={ref}>
      <Listbox value={packSelected} onChange={(value) => value && handlePackSizeChange(value)}>
        {({ open }) => (
          <>
            <Listbox.Button as="div">
              <PackLabel
                citrusAdId={citrusAdId}
                analyticsAttrs={analyticsAttrs}
                packSelected={packSelected}
                packSizes={packSizes}
                open={open}
                fromExtendedList={rest.fromExtendedList}
                screenType={screenType}
              />
            </Listbox.Button>
            <Packs {...{ elementsPosition, portalsPosition, packSelected, packSizes, open, handleQuantityChange, handleShowMore }} />
          </>
        )}
      </Listbox>
      <PackSizeModal
        listType="combo-selector"
        open={combo.open}
        onClose={handleComboClose}
        options={variations.combo}
        productCount={combo?.ids?.length}
        getProducts={() => PDService?.getComboProducts(combo.ids)}
      />
    </div>
  );
};

export default PackChanger;
